/**
 * EventBus - Sistema de eventos centralizado para el cotizador
 *
 * Permite comunicación desacoplada entre componentes:
 * - Cotizador → Carrito
 * - Carrito → Sidebar
 * - Sidebar → UI
 *
 * @version 1.0.0
 */

const EventBus = {
  _listeners: {},
  _debug: false,

  /**
   * Suscribirse a un evento
   * @param {string} eventName - Nombre del evento
   * @param {Function} callback - Función a ejecutar
   * @returns {Function} Función para desuscribirse
   */
  on(eventName, callback) {
    if (typeof callback !== 'function') {
      return () => {};
    }

    if (!this._listeners[eventName]) {
      this._listeners[eventName] = [];
    }

    this._listeners[eventName].push(callback);

    // Retornar función para desuscribirse
    return () => this.off(eventName, callback);
  },

  /**
   * Desuscribirse de un evento
   */
  off(eventName, callback) {
    if (!this._listeners[eventName]) return;

    this._listeners[eventName] = this._listeners[eventName].filter(
      (cb) => cb !== callback
    );
  },

  /**
   * Emitir un evento
   * @param {string} eventName - Nombre del evento
   * @param {*} data - Datos a pasar a los listeners
   */
  emit(eventName, data = {}) {
    if (!this._listeners[eventName]) return;

    this._listeners[eventName].forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        if (this._debug) {
          console.warn(`[EventBus] Error en listener "${eventName}":`, error);
        }
      }
    });
  },

  /**
   * Suscripción única (se auto-desuscribe después del primer evento)
   */
  once(eventName, callback) {
    const unsubscribe = this.on(eventName, (data) => {
      unsubscribe();
      callback(data);
    });
    return unsubscribe;
  },

  /**
   * Activar/desactivar modo debug (no-op en producción)
   */
  setDebug(enabled) {
    this._debug = enabled;
  }
};

/**
 * Nombres de eventos del sistema
 */
const QUOTER_EVENTS = {
  // Carrito
  CART_ITEM_ADDED: 'quoter:cart:itemAdded',
  CART_ITEM_REMOVED: 'quoter:cart:itemRemoved',
  CART_UPDATED: 'quoter:cart:updated',
  CART_CLEARED: 'quoter:cart:cleared',

  // Sincronización
  SYNC_START: 'quoter:sync:start',
  SYNC_SUCCESS: 'quoter:sync:success',
  SYNC_ERROR: 'quoter:sync:error',

  // Precios
  PRICE_CALCULATED: 'quoter:price:calculated',
  DISCOUNT_APPLIED: 'quoter:discount:applied',

  // Usuario
  USER_DETECTED: 'quoter:user:detected',
  FIRST_PURCHASE_CHECKED: 'quoter:user:firstPurchaseChecked'
};

// Exportar globalmente
if (typeof window !== 'undefined') {
  window.EventBus = EventBus;
  window.QUOTER_EVENTS = QUOTER_EVENTS;
}
